import { useState } from 'react';
import { useAuth } from '../auth/AuthContext.jsx';
import { auth as authApi } from '../api/client.js';
import { UserIcon, LockIcon, CheckIcon } from './icons.jsx';

const ROLE_OPTIONS = [
  { value: '', label: 'Mon vrai rôle', desc: 'Utiliser le rôle réel de mon compte', color: 'bg-gray-100 text-gray-700' },
  { value: 'admin', label: 'Admin', desc: 'Accès complet, gestion utilisateurs, visibilité', color: 'bg-purple-100 text-purple-700' },
  { value: 'coordinator', label: 'Coordinateur', desc: 'Tournées, stats, clients, chat, SMS', color: 'bg-blue-100 text-blue-700' },
  { value: 'driver', label: 'Livreur', desc: 'Tournées du jour, mode terrain, chat, carte', color: 'bg-emerald-100 text-emerald-700' },
];

export default function ProfilePage() {
  const { user, campaigns, activeCampaign, viewAsRole, setViewAsRole } = useAuth();
  const [pin, setPin] = useState('');
  const [pinMsg, setPinMsg] = useState('');
  const [pinLoading, setPinLoading] = useState(false);

  const campaignEntry = campaigns.find((c) => c.campaignId === activeCampaign);
  const realRole = campaignEntry?.role || 'N/A';
  const isAdmin = user?.globalRole === 'superadmin' || realRole === 'admin';

  const handleSetPin = async (e) => {
    e.preventDefault();
    if (pin.length !== 6) return;
    setPinLoading(true);
    setPinMsg('');
    try {
      await authApi.setPin(pin);
      setPinMsg('PIN mis à jour');
      setPin('');
    } catch (err) {
      setPinMsg(err.message);
    } finally {
      setPinLoading(false);
    }
  };

  return (
    <div className="animate-fade-in max-w-lg mx-auto space-y-5">
      <h2 className="text-2xl font-bold text-[#0f172a]">Profil et paramètres</h2>

      {/* User info */}
      <div className="bg-white rounded-2xl p-5 shadow-sm">
        <div className="flex items-center gap-4 mb-4">
          <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#1e40af] to-[#0891b2] flex items-center justify-center text-white text-lg font-bold">
            {(user?.name || 'U').split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase()}
          </div>
          <div>
            <div className="font-bold text-[#0f172a]">{user?.name || 'Utilisateur'}</div>
            <div className="text-sm text-[#64748b]">{user?.email}</div>
            <div className="flex items-center gap-2 mt-1">
              <span className="text-xs bg-blue-50 text-[#1e40af] px-2 py-0.5 rounded-full font-medium">
                {realRole}
              </span>
              {user?.globalRole === 'superadmin' && (
                <span className="text-xs bg-purple-50 text-purple-700 px-2 py-0.5 rounded-full font-medium">
                  superadmin
                </span>
              )}
            </div>
          </div>
        </div>
        {campaignEntry && (
          <div className="text-sm text-[#64748b] bg-gray-50 rounded-xl px-3 py-2">
            Campagne : <span className="font-medium text-[#0f172a]">{campaignEntry.campaignName}</span>
          </div>
        )}
      </div>

      {/* View as role (admin only) */}
      {isAdmin && (
        <div className="bg-white rounded-2xl p-5 shadow-sm">
          <div className="flex items-center gap-2 mb-3">
            <UserIcon size={18} className="text-[#1e40af]" />
            <h3 className="text-sm font-semibold text-[#0f172a]">Voir l'interface en tant que...</h3>
          </div>
          <p className="text-xs text-[#64748b] mb-4">
            Simulez un autre rôle pour voir l'application comme un coordinateur ou un livreur. Cela modifie uniquement l'affichage, pas vos permissions réelles.
          </p>
          <div className="space-y-2">
            {ROLE_OPTIONS.map((opt) => {
              const selected = (viewAsRole || '') === opt.value;
              return (
                <button
                  key={opt.value}
                  onClick={() => setViewAsRole(opt.value || null)}
                  className={`w-full flex items-center gap-3 p-3 rounded-xl border transition-all text-left ${
                    selected
                      ? 'border-[#1e40af] bg-blue-50 ring-1 ring-[#1e40af]/20'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className={`w-5 h-5 rounded-full flex items-center justify-center border-2 shrink-0 ${
                    selected ? 'bg-[#1e40af] border-[#1e40af]' : 'border-gray-300'
                  }`}>
                    {selected && <CheckIcon size={12} className="text-white" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-[#0f172a]">{opt.label}</span>
                      {opt.value && (
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${opt.color}`}>
                          {opt.value}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-[#64748b]">{opt.desc}</p>
                  </div>
                </button>
              );
            })}
          </div>
          {viewAsRole && (
            <div className="mt-3 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2 text-xs text-amber-700">
              Mode simulation actif : vous voyez l'interface <strong>{viewAsRole}</strong>. Vos permissions réelles sont inchangées.
            </div>
          )}
        </div>
      )}

      {/* PIN setup */}
      <div className="bg-white rounded-2xl p-5 shadow-sm">
        <div className="flex items-center gap-2 mb-3">
          <LockIcon size={18} className="text-[#f97316]" />
          <h3 className="text-sm font-semibold text-[#0f172a]">PIN rapide</h3>
        </div>
        <p className="text-xs text-[#64748b] mb-3">
          Définissez un PIN à 6 chiffres pour vous connecter plus rapidement.
        </p>
        <form onSubmit={handleSetPin} className="flex gap-2">
          <input
            type="tel"
            inputMode="numeric"
            pattern="[0-9]{6}"
            maxLength={6}
            placeholder="6 chiffres"
            value={pin}
            onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
            className="flex-1 px-3 py-3 border border-gray-200 rounded-xl text-sm text-center tracking-[0.3em] bg-gray-50 focus:bg-white focus:ring-2 focus:ring-[#1e40af] focus:border-transparent outline-none"
          />
          <button
            type="submit"
            disabled={pin.length !== 6 || pinLoading}
            className="bg-[#1e40af] text-white px-5 py-3 rounded-xl text-sm font-medium disabled:opacity-50"
          >
            {pinLoading ? '...' : 'Valider'}
          </button>
        </form>
        {pinMsg && (
          <p className={`text-xs mt-2 ${pinMsg.includes('mis à jour') ? 'text-[#059669]' : 'text-[#dc2626]'}`}>
            {pinMsg}
          </p>
        )}
      </div>

      {/* App info */}
      <div className="text-center text-xs text-gray-400 py-4">
        Velogistique v1 — Logistique terrain
      </div>
    </div>
  );
}
